import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Search as SearchIcon, Users, Disc3, Music, Film, Tv, Loader2, ChevronRight } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { search } from '../api/client'
import type { SearchResult } from '../types'

const contentTypeIcons: Record<string, React.ElementType> = {
  artist: Users,
  album: Disc3,
  track: Music,
  movie: Film,
  episode: Tv,
  series: Tv,
}

const contentTypeLabels: Record<string, string> = {
  artist: 'Artists',
  album: 'Albums',
  track: 'Songs',
  movie: 'Movies',
  series: 'TV Shows',
  episode: 'Episodes',
}

const sectionOrder = ['artist', 'track', 'album', 'movie', 'series', 'episode']

const contentTypeRoutes: Record<string, (id: string, parentId?: string) => string | null> = {
  artist: (id) => `/artists/${id}`,
  album: (id) => `/albums/${id}`,
  track: (_id, parentId) => parentId ? `/albums/${parentId}` : null,
  movie: (id) => `/movies/${id}`,
  series: (id) => `/tv/${id}`,
  episode: (_id, parentId) => parentId ? `/tv/${parentId}` : null,
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const initialQuery = searchParams.get('q') || ''
  const filterType = searchParams.get('type') || ''
  const [query, setQuery] = useState(initialQuery)
  const [inputValue, setInputValue] = useState(initialQuery)

  // Sync query with URL params
  useEffect(() => {
    const urlQuery = searchParams.get('q') || ''
    setQuery(urlQuery)
    setInputValue(urlQuery)
  }, [searchParams])

  // Search with high limit for full page results
  const { data: results, isLoading } = useQuery({
    queryKey: ['search-page', query],
    queryFn: () => search(query, 200),
    enabled: query.length >= 2,
  })

  // Group results by content type
  const groupedResults = results?.reduce<Record<string, SearchResult[]>>((acc, result) => {
    const type = result.content_type
    if (!acc[type]) acc[type] = []
    acc[type].push(result)
    return acc
  }, {})

  // Filter results if a type is specified
  const filteredResults = filterType && groupedResults
    ? { [filterType]: groupedResults[filterType] || [] }
    : groupedResults

  const handleResultClick = (result: SearchResult) => {
    const getRoute = contentTypeRoutes[result.content_type]
    if (getRoute) {
      const route = getRoute(result.id, result.parent_id)
      if (route) {
        navigate(route)
      }
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (inputValue.trim()) {
      setSearchParams({ q: inputValue.trim(), ...(filterType && { type: filterType }) })
    }
  }

  const handleTypeFilter = (type: string | null) => {
    if (type) {
      setSearchParams({ q: query, type })
    } else {
      setSearchParams({ q: query })
    }
  }

  const totalResults = results?.length || 0

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-dark-100">Search</h1>
        <p className="text-dark-400 mt-1">Find artists, albums, songs, movies, and TV shows</p>
      </div>

      {/* Search Input */}
      <form onSubmit={handleSearch} className="relative max-w-2xl">
        <SearchIcon
          size={20}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-400"
        />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Search for anything..."
          className="w-full pl-12 pr-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-lg"
          autoFocus
        />
      </form>

      {/* Type Filter Tabs */}
      {query.length >= 2 && groupedResults && Object.keys(groupedResults).length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleTypeFilter(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !filterType
                ? 'bg-primary-600 text-white'
                : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
            }`}
          >
            All ({totalResults})
          </button>
          {sectionOrder.map((type) => {
            const items = groupedResults[type]
            if (!items || items.length === 0) return null
            const Icon = contentTypeIcons[type]
            const label = contentTypeLabels[type]
            return (
              <button
                key={type}
                onClick={() => handleTypeFilter(type)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  filterType === type
                    ? 'bg-primary-600 text-white'
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
                }`}
              >
                <Icon size={16} />
                {label} ({items.length})
              </button>
            )
          })}
        </div>
      )}

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={32} className="animate-spin text-primary-400" />
        </div>
      ) : query.length < 2 ? (
        <div className="text-center py-16 text-dark-400">
          Enter at least 2 characters to search
        </div>
      ) : !results || results.length === 0 ? (
        <div className="text-center py-16 text-dark-400">
          No results found for "{query}"
        </div>
      ) : (
        <div className="space-y-8">
          {sectionOrder.map((type) => {
            const items = filteredResults?.[type]
            if (!items || items.length === 0) return null

            const Icon = contentTypeIcons[type] || Music
            const label = contentTypeLabels[type] || type

            return (
              <section key={type}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-dark-100">
                    <Icon size={20} className="text-primary-400" />
                    {label}
                    <span className="text-dark-500 font-normal">({items.length})</span>
                  </h2>
                  {!filterType && items.length > 5 && (
                    <button
                      onClick={() => handleTypeFilter(type)}
                      className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1"
                    >
                      See all {items.length}
                      <ChevronRight size={16} />
                    </button>
                  )}
                </div>

                <div className="grid gap-2">
                  {(filterType ? items : items.slice(0, 10)).map((result) => (
                    <button
                      key={`${result.content_type}-${result.id}`}
                      onClick={() => handleResultClick(result)}
                      className="flex items-center gap-4 p-3 bg-dark-800 hover:bg-dark-700 rounded-lg text-left transition-colors"
                    >
                      <div className="w-12 h-12 bg-dark-600 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {result.image_path ? (
                          <img
                            src={`/api/image/${encodeURIComponent(result.image_path)}`}
                            alt=""
                            className="w-full h-full object-cover" loading="lazy"
                          />
                        ) : (
                          <Icon size={24} className="text-dark-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-dark-100 truncate font-medium">{result.title}</p>
                        {result.subtitle && (
                          <p className="text-dark-400 truncate text-sm">{result.subtitle}</p>
                        )}
                      </div>
                      <ChevronRight size={20} className="text-dark-500 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}
